/**
 * navModel — the DECLARATIVE nav tree (labels, sections, grouping, role gating, hrefs).
 *
 * Extracted out of components/layout/navItems.tsx (2026-10-07, Customer menu redesign Phase 1)
 * so the parts with real rules are a plain `.ts` file the Node test runner can import with no
 * React/JSX harness (see navModel.test.ts). navItems.tsx only attaches the glyphs.
 *
 * NAMING (owner rule): the tenancy level between the MSP and a Site is a **Customer**. The UI
 * never says "Org" / "Organization" (that is an internal/API word) and never "Building" (a site
 * is not always a building). Hierarchy wording is MSP -> Customer -> Site. navModel.test.ts
 * enforces this on every label.
 *
 * SLUGS ARE STABLE. The redesign renamed LABELS only (Audit -> Change log, Operators -> Team,
 * Network Users -> Wi-Fi users, Firmware Compliance -> Firmware status, Firmware -> Firmware
 * upgrades, Services -> Site services). Every route section slug is unchanged, so no bookmark
 * or deep link breaks and no redirect is needed; LEGACY_SECTIONS pins that.
 *
 * OP#1858 (2026-10-18, user feedback 2026-10-14) again renamed LABELS only, to make every leaf
 * label unique across the menu: AP / Switch Port Profiles, VLAN Library vs Site VLANs, Firewall
 * Status vs Firewall Rules, SSID Firewall & Rate Limits, Wi-Fi Access Lists vs Gateway Access
 * Control. It added Customer > Monitor > Alerts, hid "soon" leaves from customer roles, and made
 * activeSection() the one highlight rule (see SITE_SERVICES_TABS).
 */

/** Which group a leaf sits in. "MSP" holds the msp_admin-only cross-customer entries. */
export type NavGroupLabel = "Monitor" | "Configure" | "MSP";

export interface NavLeafModel {
  /** Route section segment, appended under the current scope. */
  section: string;
  label: string;
  status?: "soon";
  /** Visible to msp_admin only (never shown to org_admin / site_operator / read_only). */
  mspOnly?: boolean;
  /** Absolute href that ignores scope entirely (e.g. the MSP portal at /customers). */
  href?: string;
  /**
   * Always link at CUSTOMER level, clearing any selected site. The Overview is a table of
   * every site, so "Overview of one site" is meaningless; clicking it from a site page widens
   * the header scope back to "All sites" (the same thing the scope bar's "All sites" does).
   */
  customerLevel?: boolean;
  /** Optional small heading this leaf is listed under inside its group (e.g. "Inventory"). */
  subgroup?: string;
}

export interface NavGroupModel {
  label: NavGroupLabel;
  items: NavLeafModel[];
}

export interface NavDomainModel {
  id: string;
  label: string;
  groups: NavGroupModel[];
}

/**
 * The domain tree. The first domain is the **Customer** menu (it replaced the separate
 * "Network-wide" and "Network" domains): everything that is about the customer as a whole --
 * monitoring across its sites, the people, inventory, firmware, authentication, alerting.
 * Wireless / Hospitality / Switch / Gateway are unchanged.
 */
export const NAV_MODEL: NavDomainModel[] = [
  {
    id: "customer",
    label: "Customer",
    groups: [
      {
        label: "Monitor",
        items: [
          { section: "overview", label: "Overview", customerLevel: true },
          { section: "dashboard", label: "Health" },
          // The open-alert list (routes/Alerts, OP#1854 / !266). It was reachable only from an
          // alert COUNT (Health tiles, Customers / Overview cells); OP#1858 gives it a menu
          // entry so "where are my alerts?" has an answer without a non-zero count to click.
          // Every role sees it (the list is read-only and server-scoped to the viewer).
          { section: "alerts", label: "Alerts" },
          { section: "devices", label: "Devices" },
          { section: "clients", label: "Clients" },
          { section: "usage", label: "Usage" },
          { section: "topology", label: "Topology" },
          { section: "audit", label: "Change log" },
          { section: "firmware-compliance", label: "Firmware status" },
        ],
      },
      {
        label: "Configure",
        items: [
          { section: "sites", label: "Sites" },
          { section: "operators", label: "Team" },
          { section: "network-users", label: "Wi-Fi users" },
          { section: "onboard", label: "Onboard Devices", subgroup: "Inventory" },
          { section: "device-import", label: "Import from Workbook", subgroup: "Inventory" },
          { section: "templates", label: "Templates" },
          { section: "firmware", label: "Firmware upgrades" },
          { section: "radius-servers", label: "RADIUS Servers", subgroup: "Authentication" },
          { section: "radius-realms", label: "RADIUS Realms", subgroup: "Authentication" },
          { section: "notifications", label: "Notifications" },
          // Site-wide service actions (LLDP, device password, WAN watchdog) -- routes/SiteConfigure.
          { section: "configure", label: "Site services" },
        ],
      },
      {
        label: "MSP",
        items: [
          // The cross-customer portal (routes/Customers). Also reachable from the scope bar's
          // back chevron, but an icon-only control is not a menu; msp_admin only.
          { section: "customers", label: "All customers", mspOnly: true, href: "/customers" },
        ],
      },
    ],
  },
  {
    id: "wireless",
    label: "Wireless",
    groups: [
      {
        label: "Monitor",
        items: [
          { section: "access-points", label: "Access Points" },
          { section: "rf-spectrum", label: "RF Spectrum" },
        ],
      },
      {
        label: "Configure",
        items: [
          { section: "ssids", label: "SSIDs" },
          // OP#1858 disambiguation: every leaf label is unique across the WHOLE menu (pinned by
          // navModel.test.ts). Wall-plate AP port maps, not switch-port configs.
          { section: "port-profiles", label: "AP Port Profiles" },
          // MAC/IP access lists enforced at the AP (Gateway has its own, unbuilt, edge policy).
          { section: "access-control", label: "Wi-Fi Access Lists" },
          // Per-SSID L3/L4 policy + bandwidth cap, enforced by the AP. "Traffic Shaping" is the
          // GATEWAY's (unbuilt) edge queueing, so the word is not reused here.
          { section: "firewall", label: "SSID Firewall & Rate Limits" },
          { section: "radio", label: "Radio Settings" },
          { section: "mesh", label: "Mesh" },
          { section: "multi-psk", label: "Multi-PSK" },
        ],
      },
    ],
  },
  {
    id: "hospitality",
    label: "Hospitality",
    groups: [
      {
        label: "Configure",
        items: [
          { section: "guest-portal", label: "Guest Portal" },
          { section: "passpoint", label: "Passpoint" },
          { section: "vouchers", label: "Vouchers" },
          { section: "pms", label: "PMS Integration" },
          { section: "walled-gardens", label: "Walled Gardens" },
        ],
      },
    ],
  },
  {
    id: "switch",
    label: "Switch",
    groups: [
      {
        label: "Monitor",
        items: [
          { section: "switches", label: "Switches" },
          { section: "switch-ports", label: "Ports" },
          { section: "multicast", label: "Multicast" },
        ],
      },
      {
        label: "Configure",
        items: [
          // The org-wide catalog of VLAN definitions that Switch Port Profiles and SSIDs reference
          // by id (routes/SwitchVlans). NOT the site VLAN table that deploys (Gateway > Site
          // VLANs) -- hence "Library". Kept (not redirected) because Switch Port Profiles' VLAN
          // picker reads this catalog; folding it into the site table is #1829.
          { section: "switch-vlans", label: "VLAN Library" },
          { section: "switch-poe", label: "PoE" },
          { section: "switch-portprofiles", label: "Switch Port Profiles" },
        ],
      },
    ],
  },
  {
    // The site edge: uCentral whitebox gateways and firewalls, shaped like a Meraki MX
    // (Security & SD-WAN). Unbuilt screens carry status "soon" and route to ComingSoon, which
    // states per screen what it still needs (see navItems.tsx history / routes/ComingSoon).
    id: "gateway",
    label: "Gateway",
    groups: [
      {
        label: "Monitor",
        items: [
          { section: "gateways", label: "Gateway Status" },
          // The firewall DEVICE list (Devices filtered to kind=firewall) -- a status view, the
          // sibling of "Gateway Status". Was "Firewalls", one letter from "Firewall" below.
          { section: "firewalls", label: "Firewall Status" },
          { section: "security-center", label: "Security Posture" },
          { section: "route-table", label: "Route Table" },
        ],
      },
      {
        label: "Configure",
        items: [
          // The SITE VLAN table, deployed to every gateway and switch (routes/GatewayVlans). Also
          // the "VLANs" tab of Customer > Site services (served at configure/vlans).
          { section: "gateway-vlans", label: "Site VLANs" },
          { section: "gateway-dhcp", label: "DHCP" },
          // Port forwards, guest isolation, inter-network blocks (routes/GatewayFirewall).
          { section: "gateway-firewall", label: "Firewall Rules" },
          { section: "gateway-routing", label: "Routing", status: "soon" },
          { section: "traffic-shaping", label: "Traffic Shaping", status: "soon" },
          { section: "threat-protection", label: "Threat Protection", status: "soon" },
          { section: "content-filtering", label: "Content Filtering" },
          { section: "gateway-access-control", label: "Gateway Access Control", status: "soon" },
        ],
      },
    ],
  },
];

/**
 * Every section slug that was in the nav BEFORE the Customer menu redesign (2026-10-07).
 * Each must still be a routed section: bookmarks and deep links point at these. Pinned by
 * navModel.test.ts against both this model and router.tsx.
 */
export const LEGACY_SECTIONS: readonly string[] = [
  // former "Network-wide"
  "dashboard", "sites", "devices", "clients", "usage", "topology", "audit",
  "onboard", "device-import", "operators", "network-users",
  // former "Network"
  "configure", "templates", "radius-servers", "radius-realms", "notifications",
  "firmware", "firmware-compliance",
];

/**
 * The tree as a given viewer may see it:
 *   * msp-only leaves removed for everyone but msp_admin;
 *   * "soon" leaves (unbuilt screens that route to ComingSoon) removed for everyone but
 *     msp_admin too (OP#1858: "hide SOON for customer roles pre-GA") -- a customer admin or
 *     site manager should not be handed a menu of placeholders. The ROUTES stay, so an MSP
 *     operator can still show a customer the roadmap page by link;
 *   * any group left empty by that removed too (no orphan "MSP" heading for an org_admin).
 * Generic so navItems.tsx can pass its icon-bearing tree through the same rule.
 */
export function visibleTree<
  L extends NavLeafModel,
  G extends { label: NavGroupLabel; items: L[] },
  D extends { groups: G[] },
>(tree: D[], isMspAdmin: boolean): D[] {
  return tree
    .map((domain) => ({
      ...domain,
      groups: domain.groups
        .map((g) => ({
          ...g,
          items: g.items.filter((i) => isMspAdmin || (!i.mspOnly && i.status !== "soon")),
        }))
        .filter((g) => g.items.length > 0),
    }))
    .filter((domain) => domain.groups.length > 0);
}

/**
 * The nav SECTION a pathname is showing: the first segment after the scope prefix.
 *   /o/:org/<section>/...            -> <section>
 *   /o/:org/s/:site/<section>/...    -> <section>
 *   /o/:org                          -> "overview" (the customer index IS the Overview)
 *   anything else (/customers, /login, /o/:org/s/:site) -> null
 * One rule for every "which menu item is this page?" question (DomainRail domain + leaf
 * highlight, SiteTabs, ScopeBar site switch), so they can never disagree. It replaced
 * `pathname.split("/").includes(section)`, which matched ANY segment -- e.g. the Site services
 * tab path configure/mesh would have lit both Customer (configure) and Wireless (mesh).
 */
export function activeSection(pathname: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "o" || parts.length < 2) return null;
  if (parts.length === 2) return "overview";
  return parts[sectionIndex(parts)] ?? null;
}

/** Index of the section segment in a split /o/... path (after /o/:org or /o/:org/s/:site). */
function sectionIndex(parts: string[]): number {
  return parts[2] === "s" ? 4 : 2;
}

/** Is this leaf the page being shown? Absolute-href leaves match their path exactly. */
export function isLeafActive(leaf: Pick<NavLeafModel, "section" | "href">, pathname: string): boolean {
  if (leaf.href) return pathname === leaf.href;
  return activeSection(pathname) === leaf.section;
}

/**
 * Customer > Site services tabs (components/site/SiteTabs). ALL of them live under the
 * "configure" section -- configure, configure/vlans, configure/mesh -- so moving between tabs
 * never moves the menu highlight (OP#1858: it used to jump Customer -> Gateway -> Wireless,
 * because the tabs linked to gateway-vlans and mesh). The same screens stay reachable from
 * their own menus (Gateway > Site VLANs, Wireless > Mesh) WITHOUT the tab strip there: the strip
 * belongs to Site services, and it is shown exactly when Site services is the lit menu item.
 * `sub` is the path below "configure" ("" = the Services tab itself).
 */
export const SITE_SERVICES_TABS = [
  { id: "services", label: "Services", sub: "" },
  { id: "vlans", label: "VLANs", sub: "vlans" },
  { id: "mesh", label: "Mesh", sub: "mesh" },
] as const;

export type SiteServicesTabId = (typeof SITE_SERVICES_TABS)[number]["id"];

/**
 * Which Site services tab a pathname is on, or null when it is not a Site services page
 * (e.g. /gateway-vlans or /mesh reached from their own menus -> no tab strip).
 */
export function siteServicesTab(pathname: string): SiteServicesTabId | null {
  if (activeSection(pathname) !== "configure") return null;
  const parts = pathname.split("/").filter(Boolean);
  // Positional, never indexOf("configure"): an id segment could in theory spell anything.
  const sub = parts[sectionIndex(parts) + 1] ?? "";
  return SITE_SERVICES_TABS.find((t) => t.sub === sub)?.id ?? null;
}

/**
 * The concrete href for a leaf under the current scope.
 *   * no customer in scope          -> "/" (RootRedirect picks one; matches the old behaviour)
 *   * leaf.href                     -> that absolute path (scope-free)
 *   * site selected                 -> /o/:org/s/:site/<section>, unless customerLevel
 *   * customer level / no site      -> /o/:org/<section>
 * "dashboard" is NOT special-cased any more: the customer index (/o/:org) now lands on the
 * Overview, so Health needs its explicit /dashboard path to stay reachable.
 */
export function scopedHref(
  leaf: Pick<NavLeafModel, "section" | "href" | "customerLevel">,
  orgId: string | null,
  siteId: string | null,
): string {
  if (leaf.href) return leaf.href;
  if (!orgId) return "/";
  if (siteId && !leaf.customerLevel) return `/o/${orgId}/s/${siteId}/${leaf.section}`;
  return `/o/${orgId}/${leaf.section}`;
}

/** All in-scope route sections (for scope-preserving site switches in the ScopeBar). The
 *  absolute-href MSP portal is excluded: it is not a section under a scope. */
export const KNOWN_SECTIONS: string[] = [
  ...new Set(
    NAV_MODEL.flatMap((d) =>
      d.groups.flatMap((g) => g.items.filter((i) => !i.href).map((i) => i.section)),
    ),
  ),
];

/** The sections the tablet thumb-rail shows (space-limited; five targets at 44px+). */
export const NAV_PRIMARY_SECTIONS: { section: string; label: string; customerLevel?: boolean }[] = [
  { section: "overview", label: "Overview", customerLevel: true },
  { section: "dashboard", label: "Health" },
  { section: "devices", label: "Devices" },
  { section: "clients", label: "Clients" },
  { section: "configure", label: "Services" },
];

/** Words the UI must never show for the customer/site levels (owner naming rule). */
export const FORBIDDEN_LABEL_WORDS = /\b(org|orgs|organization|organizations|organisation|building|buildings)\b/i;
